import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Activity, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("doctor@shushrut.ai");
  const [password, setPassword] = useState("Shushrut@2026");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/dashboard");
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left */}
      <div className="hidden lg:flex relative soft-gradient p-12 flex-col justify-between">
        <Link to="/" className="flex items-center gap-2.5 w-fit" data-testid="login-logo">
          <div className="h-9 w-9 rounded-xl bg-slate-900 flex items-center justify-center">
            <Activity className="h-4 w-4 text-sky-200" strokeWidth={2.5} />
          </div>
          <span className="font-heading text-lg font-semibold tracking-tight text-slate-900">
            Shushrut<span className="text-sky-500">.</span>AI
          </span>
        </Link>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-mono-num text-slate-500 mb-4">
            For clinicians, by clinicians.
          </div>
          <h2 className="font-heading text-4xl tracking-tight text-slate-900 font-light leading-[1.1] max-w-md">
            A second opinion in seconds — <em className="not-italic text-slate-400">with a trail you can trust</em>.
          </h2>
          <div className="mt-8 grid grid-cols-3 gap-5 max-w-md">
            {[
              { k: "Accuracy", v: "88.7%" },
              { k: "Agents", v: "4" },
              { k: "Latency", v: "<2s" },
            ].map((s) => (
              <div key={s.k} className="border-t border-slate-300 pt-3">
                <div className="text-[10px] uppercase tracking-[0.25em] font-mono-num text-slate-500">{s.k}</div>
                <div className="font-mono-num text-xl text-slate-900 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="text-xs text-slate-500 font-mono-num tracking-wider">
          © Shushrut AI · Research Preview
        </div>
      </div>

      {/* Right */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-10" data-testid="login-back">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500">Sign in</div>
          <h1 className="font-heading mt-2 text-4xl tracking-tight text-slate-900 font-light">Welcome back.</h1>
          <p className="mt-3 text-slate-600">Access your doctor dashboard and patient reports.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] font-mono-num text-slate-500">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-slate-300"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] font-mono-num text-slate-500">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-slate-300"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-slate-500">
            New here?{" "}
            <Link to="/register" className="text-slate-900 font-medium hover:underline" data-testid="login-register-link">
              Create a doctor account
            </Link>
          </div>

          <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600">
            <div className="font-mono-num uppercase tracking-[0.2em] text-[10px] text-slate-500 mb-1">Demo credentials</div>
            doctor@shushrut.ai · Shushrut@2026
          </div>
        </div>
      </div>
    </div>
  );
}
